/**
 * @file src/utils/errorText.js
 * @description The sentence a failed request is shown as.
 *
 * Twenty-eight screens carried their own copy of
 *   `e?.response?.data?.error?.code || e?.message || 'Failed'`
 * and eleven of them put the CODE before the message, so a validation
 * error reached a reviewer as `VALIDATION_FAILED` or the single word
 * "Failed". The server's message is what a person can act on; the code
 * is for support, and is appended in brackets only when the message is
 * missing — never instead of it.
 */

const clean = (v) => (v == null ? '' : String(v).trim());

/**
 * @param {unknown} e            anything a catch block receives
 * @param {string}  fallback     what to say when the error carries nothing usable
 * @returns {string}
 */
export const RESTARTING = 'Moksy is restarting — give it a few seconds and try again.';
export const CONNECTION_DROPPED = 'We could not reach Moksy just now — it may be your connection or ours. Try again in a moment.';
export const TOOK_TOO_LONG = 'Moksy is taking longer than usual to answer. Try again in a moment.';

// An infrastructure failure is never shown as the string the socket produced.
// The reviewer work console printed "connect ECONNREFUSED 172.20.0.5:5014" to a
// reviewer mid-submit on 2026-09-05 while the report page, through its own
// mapper, said "Moksy is restarting". One sentence, here, for every screen
// that reads errorText (platform law EX-LIVE: the same failure reads the same
// everywhere; an address and a port are for an engineer).
const infrastructureText = (e) => {
  const status = Number(e?.response?.status) || 0;
  const err = e?.response?.data?.error || e?.response?.data || {};
  const blob = [e?.code, e?.message, err.code, err.message].map((v) => (v == null ? '' : String(v))).join(' ');
  if (status === 502 || status === 503 || status === 504) return RESTARTING;
  if (/ECONNREFUSED|ECONNRESET|EAI_AGAIN|socket hang up|SERVICE_UNAVAILABLE/i.test(blob)) return RESTARTING;
  if (!e?.response && /timeout of \d+ms exceeded|ECONNABORTED/i.test(blob)) return TOOK_TOO_LONG;
  if (!e?.response && /Network Error|ERR_NETWORK/i.test(blob)) return CONNECTION_DROPPED;
  return null;
};

/**
 * A validation failure raised in the BROWSER, before the request was sent.
 *
 * ⚠️ The dashboard mirrors each service's Zod contract in its own API client
 * (`BillingUpdateSchema.parse(payload)` in api/identity.js and 19 siblings), so
 * a rejected field throws a ZodError HERE — with no `.response`, and with a
 * `.message` that is the whole issues array stringified. A customer saving a
 * long first line of their billing address was shown, in the red panel under
 * the form:
 *
 *   [ { "origin": "string", "code": "too_big", "maximum": 160,
 *       "inclusive": true, "path": [ "line1" ],
 *       "message": "Too big: expected string to have <=160 characters" } ]
 *
 * The services themselves get this right — the users-service handler drops a
 * ZodError's message precisely because it is "several hundred characters of
 * internals" and sends a per-field list instead. Only the browser-side copy
 * had no such translation.
 *
 * Duck-typed rather than `instanceof z.ZodError`: this file must not depend on
 * zod, and v3 (`.errors`) and v4 (`.issues`) both answer to this shape.
 */
const zodIssues = (e) => {
  const issues = e?.issues || e?.errors;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  // `errors` on an API envelope is our own {field, message} list, not Zod's.
  return issues.every((i) => i && typeof i === 'object' && 'code' in i) ? issues : null;
};

// `postalCode` → "Postal code", `line1` → "Line 1", `taxId` → "Tax ID".
const fieldLabel = (path) => {
  const key = (Array.isArray(path) ? path : [path]).filter((p) => typeof p !== 'number').join(' ');
  if (!key) return 'That value';
  const words = String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/[._]/g, ' ')
    .trim()
    .toLowerCase();
  const cased = words.charAt(0).toUpperCase() + words.slice(1);
  return cased.replace(/\bid\b/i, 'ID').replace(/\burl\b/i, 'URL');
};

// Zod's own wording is written for whoever wrote the schema ("Too big:
// expected string to have <=160 characters"). These say the same limit the way
// the person filling the field would.
const issueSentence = (issue) => {
  const label = fieldLabel(issue?.path);
  const max = issue?.maximum;
  const min = issue?.minimum;
  switch (issue?.code) {
    case 'too_big':
      return Number.isFinite(Number(max))
        ? `${label} must be ${max} characters or fewer.`
        : `${label} is too long.`;
    case 'too_small':
      return Number(min) <= 1
        ? `${label} is required.`
        : `${label} must be at least ${min} characters.`;
    case 'invalid_type':
      return issue?.received === 'undefined' || issue?.received == null
        ? `${label} is required.`
        : `${label} is not in the expected format.`;
    case 'invalid_enum_value':
    case 'invalid_value':
      return `${label} is not one of the accepted values.`;
    case 'unrecognized_keys':
      return 'Some of that information is not accepted here.';
    case 'invalid_format':
    case 'invalid_string':
      return issue?.validation === 'email' || issue?.format === 'email'
        ? `${label} must be a valid email address.`
        : `${label} is not in the expected format.`;
    default: {
      const own = clean(issue?.message);
      return own ? `${label}: ${own}` : `${label} is not valid.`;
    }
  }
};

/**
 * One sentence for a client-side validation failure.
 * One bad field is named outright; several are listed, because a person
 * fixing a form needs to know every field to go back to, not just the first.
 */
export const validationText = (e) => {
  const issues = zodIssues(e);
  if (!issues) return null;
  const sentences = [...new Set(issues.map(issueSentence))];
  if (sentences.length === 1) return sentences[0];
  return sentences.slice(0, 3).join(' ') + (sentences.length > 3 ? ' …and more.' : '');
};

export function errorText(e, fallback = 'Something went wrong. Try again.') {
  // Before anything else: a failure raised in the browser never reached a
  // server, so it has no status, no code and no envelope — only a message that
  // is machine-readable JSON.
  const validation = validationText(e);
  if (validation) return validation;
  const infra = infrastructureText(e);
  if (infra) return infra;
  const err = e?.response?.data?.error || e?.response?.data || {};
  // Axios's own "Request failed with status code 400" is not a message a
  // person can act on; it is skipped so the fallback sentence is said instead.
  const generic = (s) => /^request failed with status code \d+$/i.test(s);
  const own = clean(e?.message);
  const message = clean(err.message) || clean(e?.details?.message) || (generic(own) ? '' : own);
  // A transport-level error (no envelope) carries its own code — `e.code` is
  // where axios puts ERR_BAD_REQUEST and where our own thrown errors put
  // theirs. Read as a last resort so the bracketed reference is never empty
  // when the caller had one to give.
  const code = clean(err.code) || clean(e?.code);
  // A bare code is not a message. A message that IS a code (all caps and
  // dots/underscores) gets the fallback sentence in front of it.
  const looksLikeCode = (s) => /^[A-Z0-9_.]+$/.test(s) && s.length > 3;
  if (message && !looksLikeCode(message)) return message;
  // Several callers passed `|| ''` as their fallback, meaning "say nothing".
  // An empty fallback with a code would render as a bare " (V2.ADMIN_ONLY)" —
  // the exact leading-bracket nonsense this file exists to stop — so an empty
  // one falls back to the default sentence rather than to nothing.
  const said = clean(fallback) || 'Something went wrong. Try again.';
  const ref = code || (looksLikeCode(message) ? message : '');
  return ref ? `${said} (${ref})` : said;
}

export default errorText;
